// The ONE navigation truth. Every menu — landing, dashboard, docs, company,
// admin — reads its spaces from here, so "where can I go from anywhere" has a
// single, editable source. The menu is a PSP/XMB-style multi-space navigator:
//
//   space 1 · THIS PAGE   — the sections inside the surface you're on
//                           (label per surface: "yours" on the dashboard,
//                           "company", "docs", "home" on the landing).
//   space 2 · innernet    — the public site map: every page, from anywhere.
//   space 3 · founders    — the founders' tools (only shown to founders).
//
// A NavItem either navigates (`href`) or fires a window event (`event`, e.g. the
// netti-lab modal on the dashboard) — or both, so an item can open in place
// where its target is mounted and route there otherwise.

import { gateOpen } from "../release/gates";
import { DASHBOARD_HOST } from "../subdomain";

/** THE LANDING PAGE'S ADDRESS, FROM ANYWHERE.
 *
 *  ⚠️ Not always "/". The app lives on its own host (yours.innernet.live) and
 *  proxy.ts sends that host's root straight to /dashboard — so "homepage" in the
 *  ≡ menu, which simply navigated to "/", put you back in the dashboard you were
 *  trying to leave. From the app host the homepage has to be named absolutely.
 *  Everywhere else (and whenever the host split is off) "/" is still right. */
export function homepageHref(): string {
  if (!DASHBOARD_HOST || typeof window === "undefined") return "/";
  if (window.location.host !== DASHBOARD_HOST) return "/";
  const apex = DASHBOARD_HOST.split(".").slice(1).join(".");
  return apex ? `https://${apex}/` : "/";
}

export interface NavItem {
  key: string;
  label: string;
  href?: string;
  /** dispatch this window CustomEvent on select (in-place actions like the lab) */
  event?: string;
  here?: boolean;
}

export interface NavSpace {
  id: string;
  label: string;
  items: NavItem[];
}

// space 2 — the public site map. The canonical list of innernet's own pages.
export const INNERNET_PAGES: NavItem[] = [
  { key: "home", label: "home", href: "/" },
  { key: "company", label: "company", href: "/company" },
  { key: "blog", label: "blog", href: "/company" },
  { key: "compare", label: "compare", href: "/company/compare" },
  { key: "about", label: "about", href: "/company/about" },
  { key: "team", label: "team", href: "/company/team" },
  // `/ours` is UNLISTED until its release gate opens — see lib/release/gates.ts.
  // It is spliced in below rather than listed here, so the gate is honoured in
  // one place and can't be half-flipped.
  { key: "docs", label: "docs", href: "/docs" },
  // `/community` is listed through its release gate (communityPublic, OPEN since
  // 2026-09-14) — spliced into the tree below under the gate with its three
  // spaces as children, not listed flat here (same mechanism as /ours).
  { key: "status", label: "status", href: "/status" },
  { key: "security", label: "security", href: "/security" },
  { key: "privacy", label: "privacy", href: "/privacy" },
  { key: "terms", label: "terms", href: "/terms" },
];

// space 3 — the founders' tools. `event` opens the target in place where it's
// mounted (the netti lab is a dashboard modal); `href` is the universal route.
export const FOUNDER_TOOLS: NavItem[] = [
  // innside: the founders' map of everything innernet does (app/innside)
  { key: "innside", label: "innside innernet", href: "/innside" },
  { key: "admin", label: "admin panel", href: "/admin" },
  { key: "netti-lab", label: "netti lab", href: "/netti-lab" },
  { key: "media", label: "media tool", href: "/media" },
  { key: "netti-designer", label: "netti designer", href: "/netti" },
  // the dispatch archive (the company's "inside innernet" stream), named for
  // what it holds here so it never reads as innside itself
  { key: "inside", label: "dispatches", href: "/company?tab=inside" },
  { key: "reports", label: "reports", href: "/company?tab=reports" },
];

// THE one public link row, rendered in two places: the landing's closing bar
// (components/landing/LandingSections.tsx, which swaps `contact` for its own
// options menu) and the ≡ menu's footer (components/MenuFooter.tsx). Between
// this row and the menu tree above, every page a visitor could want is one
// click away from anywhere on the site.
//
// ⚠️ A LINK TO NOWHERE IS WORSE THAN NO LINK. This row used to carry
// `developers → /#developers`: there is no `#developers` anchor on the homepage
// (the section is `#s-dev`), and `/developers` itself is only a redirect into
// the dashboard, so the one real link in the footer went nowhere. Every href
// here must resolve to a page that renders for a signed-out stranger — no
// anchors, no redirect-only routes, nothing behind a release gate (`/pricing`
// is gated; `/ours` is parked).
//
// Kept SHORT on purpose: on a phone this row wraps, and a long list turns the
// footer into a stack of rows. Eight links plus contact is the ceiling.
// The commons gets TWO doors here (John, 2026-09-17): `skills` goes straight to
// the shelf — the thing most people arriving from a reply are looking for — and
// `community` opens the hub it belongs to (skills · guides · resources). They
// are two real addresses, not one link twice: /community/skills is the skills
// space by its own name, /community is the hub's front room. (The row said
// "skills and guides" for three days, 2026-09-14 → 17, and carried nothing of
// the commons from 2026-08 until the communityPublic gate opened.)
export const FOOTER_LINKS: [string, string][] = [
  ["docs", "/docs"],
  ["skills", "/community/skills"],
  ["community", "/community"],
  ["company", "/company"],
  ["compare", "/company/compare"],
  ["security", "/security"],
  ["privacy", "/privacy"],
  ["terms", "/terms"],
  ["contact", "mailto:connect@innernet.live"],
];

// the shape a surface hands in for its OWN space — the sections of the page you
// are on. Each item can carry its own `children` (a sub-directory) so the menu
// can drill deeper (e.g. the dashboard's "projects" section opens into the
// actual project list). `onSelect` is the OPEN action (double-click / commit).
export interface MenuItem {
  key: string;
  label: string;
  here?: boolean;
  /** the OPEN action. Omit to make this a PURE DIRECTORY — the menu's tap-and-go
   *  rule opens anything that has a page, so an item that should reveal its
   *  contents instead (e.g. "ours" → the rooms) must not carry one. */
  onSelect?: () => void;
  /** sub-items — single-click drills into these inside the menu. */
  children?: MenuItem[];
}
export interface MenuSpace {
  id: string;
  label: string;
  items: MenuItem[];
}

// ── the menu is a TREE, breadcrumb-steered ──────────────────────────────────
// One root ("innernet") whose children are the spaces; each space its own
// sections; each section its own items. The menu walks this tree: single-click a
// directory drills in (a new box, deeper in the breadcrumb); double-click OPENS
// (runs onOpen, collapses the menu). A node with `children` is a directory; a
// node with `onOpen` is openable; it can be both.
export interface MenuNode {
  key: string;
  label: string;
  /** carries the living-color orb — the current location. */
  here?: boolean;
  /** a directory — single-click reveals these one level deeper. */
  children?: MenuNode[];
  /** the commit action — double-click / second-tap navigates + collapses. */
  onOpen?: () => void;
  /** the row wears the signed-in person's own cell face beside its label
   *  ("yours" — the app is theirs, so the mark says so). */
  avatar?: boolean;
  /** the row wears an empty cell — "enter", for someone not signed in yet */
  cell?: boolean;
}

const itemToNode = (it: MenuItem): MenuNode => ({
  key: it.key,
  label: it.label,
  here: it.here,
  onOpen: it.onSelect,
  children: it.children?.map(itemToNode),
});

/** perform a NavItem: fire its event (opens in place where mounted), then route
 *  to its href. `nav` lets the host use a client router; falls back to a hard
 *  navigation. An in-place event with no router (the dashboard lab) doesn't also
 *  route away — the modal wins. */
export function runNavItem(item: NavItem, nav?: (href: string) => void) {
  if (item.event) window.dispatchEvent(new CustomEvent(item.event));
  if (item.href) {
    if (item.event && !nav) return;
    if (nav) nav(item.href);
    else window.location.assign(item.href);
  }
}

/** Build the whole breadcrumb TREE for a surface, rooted at "innernet":
 *
 *    innernet
 *      ├─ yours       (the app — its sections, when you're inside it or signed in)
 *      ├─ homepage
 *      ├─ company     → blog · updates · (inside)
 *      ├─ docs
 *      ├─ status · security · privacy · terms
 *      └─ founders    (founder tools)
 *
 *  The space matching the current surface swaps in its LIVE sections (so its orb
 *  + drill are real). `startPath` is where the breadcrumb opens: the innernet
 *  root on public pages ("all the company things"), or drilled into "yours" once
 *  you're inside the app. */
export function buildMenuTree(opts: {
  /** this page's own sections — become the current space's children. */
  current: MenuSpace;
  founder?: boolean;
  /** inside the app (dashboard/admin) — surfaces "yours" and opens drilled into it. */
  signedIn?: boolean;
  /** dot + open the matching innernet page (e.g. "company"). */
  hereKey?: string;
  /** open the breadcrumb drilled to this space key (defaults to "yours" in-app). */
  startKey?: string;
  nav?: (href: string) => void;
  close: () => void;
}): { root: MenuNode; startPath: string[] } {
  const { current, founder = false, signedIn = false, hereKey, startKey, nav, close } = opts;
  const go = (href: string) => {
    close();
    // an absolute url leaves this origin (the homepage, seen from the app host):
    // a client router cannot cross origins, so it must be a real navigation.
    const external = /^https?:\/\//i.test(href);
    if (nav && !external) nav(href);
    else if (typeof window !== "undefined") window.location.assign(href);
  };

  // the current surface's live sections (each already carries its own handler,
  // its `here` dot, and — for the dashboard — its own deeper sub-tree).
  const sections = current.items.map(itemToNode);
  // ⚠️ "in the app" is about WHERE YOU ARE STANDING, never about whether you are
  // signed in. This used to read `signedIn || …`, and the cost was invisible to
  // anyone logged out: a signed-in person on the LANDING got that page's own
  // sections (connect · create · carry · netti · …) mounted under "yours", and
  // because startPath drills into "yours" whenever inApp, the ≡ opened straight
  // into a list of homepage anchors instead of the site. The homepage has no
  // deeper navigation — its sections are acts of one scroll, not places — so the
  // menu must open at the innernet root for every visitor, signed in or not.

  // company always drills into its posts surfaces; on the company page itself we
  // use the live tab sections so the current tab carries the orb.
  const companyKids: MenuNode[] =
    current.id === "company"
      ? sections
      : [
          { key: "blog", label: "blog", onOpen: () => go("/company") },
          { key: "updates", label: "updates", onOpen: () => go("/company?tab=updates") },
          { key: "compare", label: "compare", onOpen: () => go("/company/compare") },
          { key: "about", label: "about", onOpen: () => go("/company/about") },
          { key: "team", label: "team", onOpen: () => go("/company/team") },
          ...(founder ? [{ key: "inside", label: "inside innernet", onOpen: () => go("/company?tab=inside") }] : []),
          ...(founder ? [{ key: "reports", label: "reports", onOpen: () => go("/company?tab=reports") }] : []),
        ];

  // a STANDALONE surface — one the site map doesn't already carry (the deck,
  // pricing, a future room). Its sections used to vanish from the menu
  // entirely: the ≡ opened onto the site as if you were standing nowhere. Now
  // any such surface mounts its own sections as a branch, so the takeover is
  // its contents page and the parent site is one step up.
  const handled = new Set(["yours", "console", "home", "company", "docs", "community"]);
  const standalone = !handled.has(current.id) && current.items.length > 0;

  const rootChildren: MenuNode[] = [];

  // "yours" — the app, and ALWAYS the first row of the top directory, wherever
  // you are standing. Inside the dashboard it carries the dashboard's own
  // sections; anywhere else, signed in, it is one door to the dashboard wearing
  // the person's own cell face; a stranger sees the same door as "enter",
  // wearing an empty cell. The menu never claims a place is yours before it is.
  //
  // THE CONSOLE IS ITS OWN ROW, never "yours" relabelled. It used to mount the
  // console's sections under a row named "console" whose commit went to
  // /dashboard, and opened drilled into it, so a founder in the console almost
  // never saw the way back to their dashboard (and the one row that went there
  // was labelled as somewhere else).
  if (current.id === "yours") {
    rootChildren.push({ key: "yours", label: "yours", children: sections, onOpen: () => go("/dashboard"), avatar: true });
  } else if (signedIn) {
    rootChildren.push({ key: "yours", label: "yours", avatar: true, onOpen: () => go("/dashboard") });
  } else {
    rootChildren.push({ key: "yours", label: "enter", cell: true, onOpen: () => go("/signup") });
  }
  if (current.id === "console") {
    rootChildren.push({ key: "console", label: "console", here: true, children: sections, onOpen: () => go("/admin") });
  }

  if (standalone) {
    rootChildren.push({ key: current.id, label: current.label, here: true, children: sections });
  }

  rootChildren.push(
    // the HOMEPAGE never nests. Its "sections" are acts of one scrolling
    // narrative, not places — offering them as a directory made the menu open
    // into a sub-list of anchors instead of the site. Every other surface
    // (dashboard · docs · company) still drills, because there the sections
    // really are pages.
    { key: "home", label: "homepage", here: hereKey === "home", onOpen: () => go(homepageHref()) },
    { key: "company", label: "company", here: hereKey === "company", children: companyKids, onOpen: () => go("/company") },
    // unlisted until its release gate opens — but always listed for a founder,
    // and always listed when you are standing on it, so the crumb never points
    // at a page the menu claims doesn't exist (lib/release/gates.ts).
    // OURS IS PARKED until its own launch: the menu lists it only while you are
    // STANDING on it (so the crumb never lies about where you are). Not for a
    // founder, not behind the gate — one place, one rule.
    ...(hereKey === "ours"
      ? [{ key: "ours", label: "ours", here: true, onOpen: () => go("/ours") }]
      : []),
    { key: "docs", label: "docs", here: hereKey === "docs", children: current.id === "docs" ? sections : undefined, onOpen: () => go("/docs") },
    // the community — skills · guides · resources — with its three spaces as
    // children (the hub's own sections when you're standing in it).
    // Listed through its release gate (lib/release/gates.ts communityPublic,
    // OPEN since 2026-09-14 on John's call). While a gate is closed a surface
    // is listed only while you are STANDING on it, so the crumb never lies
    // about where you are — the same rule as /ours.
    ...(gateOpen("communityPublic") || hereKey === "community"
      ? [{
          key: "community",
          label: "community",
          here: hereKey === "community",
          children:
            current.id === "community"
              ? sections
              : [
                  { key: "skills", label: "skills", onOpen: () => go("/community") },
                  { key: "guides", label: "guides", onOpen: () => go("/community/guides") },
                  { key: "resources", label: "resources", onOpen: () => go("/community/resources") },
                  { key: "about", label: "about", onOpen: () => go("/community/about") },
                ],
          onOpen: () => go("/community"),
        }]
      : []),
    { key: "status", label: "status", here: hereKey === "status", onOpen: () => go("/status") },
    { key: "security", label: "security", here: hereKey === "security", onOpen: () => go("/security") },
    { key: "privacy", label: "privacy", here: hereKey === "privacy", onOpen: () => go("/privacy") },
    { key: "terms", label: "terms", here: hereKey === "terms", onOpen: () => go("/terms") },
  );

  if (founder) {
    rootChildren.push({
      key: "founders",
      label: "founders",
      children: FOUNDER_TOOLS.map((it) => ({
        key: it.key,
        label: it.label,
        onOpen: () => { close(); runNavItem(it, nav); },
      })),
    });
  }

  const root: MenuNode = { key: "innernet", label: "innernet", children: rootChildren };

  // where the breadcrumb opens. Drill into a space if asked (the dashboard opens
  // straight into "yours"); otherwise the innernet root shows every space, with
  // "yours" first. The console opens at the root for exactly that reason.
  const drillKey = startKey ?? (current.id === "yours" ? "yours" : undefined);

  const startPath = drillKey && rootChildren.some((c) => c.key === drillKey) ? ["innernet", drillKey] : ["innernet"];

  return { root, startPath };
}
